package com.trevorwiebe.timesheet.core.presentation.common

import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextFieldDefaults
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.PressInteraction
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.withFrameNanos
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation

@Composable
fun TimesheetTextField(
    modifier: Modifier = Modifier,
    text: String,
    placeholder: String = "",
    label: String = "",
    readOnly: Boolean = false,
    singleLine: Boolean = false,
    hidePasswordText: Boolean = false,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    trailingIcon: @Composable (() -> Unit)? = null,
    selectAllOnFocus: Boolean = false,
    onTextChange: (String) -> Unit = {},
) {
    if (selectAllOnFocus) {
        SelectAllOnFocusTextField(modifier, text, label, singleLine, keyboardOptions, onTextChange)
        return
    }

    OutlinedTextField(
        placeholder = if (placeholder.isNotEmpty()) {{ Text(text = placeholder) }} else null,
        label = if (label.isNotEmpty()) {{ Text(text = label) }} else null,
        modifier = modifier.fillMaxWidth(),
        value = text,
        readOnly = readOnly,
        singleLine = singleLine,
        colors = timesheetTextFieldColors(),
        onValueChange = onTextChange,
        keyboardOptions = keyboardOptions,
        keyboardActions = keyboardActions,
        trailingIcon = trailingIcon,
        visualTransformation = if (hidePasswordText) {
            PasswordVisualTransformation()
        } else {
            VisualTransformation.None
        }
    )
}

@Composable
private fun SelectAllOnFocusTextField(
    modifier: Modifier,
    text: String,
    label: String,
    singleLine: Boolean,
    keyboardOptions: KeyboardOptions,
    onTextChange: (String) -> Unit,
) {
    var selection by remember { mutableStateOf(TextRange(text.length)) }
    var justFocused by remember { mutableStateOf(false) }
    val interactionSource = remember { MutableInteractionSource() }
    // The tap that focuses the field also drops the cursor where it landed, so select everything
    // once that tap has finished.
    LaunchedEffect(interactionSource) {
        interactionSource.interactions.collect { interaction ->
            if (interaction is PressInteraction.Release && justFocused) {
                withFrameNanos { }
                selection = TextRange(0, Int.MAX_VALUE)
                justFocused = false
            }
        }
    }
    OutlinedTextField(
        label = if (label.isNotEmpty()) {{ Text(text = label) }} else null,
        modifier = modifier.fillMaxWidth().onFocusChanged {
            justFocused = it.isFocused
            if (it.isFocused) selection = TextRange(0, Int.MAX_VALUE)
        },
        value = TextFieldValue(text, TextRange(selection.start.coerceAtMost(text.length), selection.end.coerceAtMost(text.length))),
        singleLine = singleLine,
        colors = timesheetTextFieldColors(),
        interactionSource = interactionSource,
        onValueChange = {
            selection = it.selection
            if (it.text != text) {
                justFocused = false
                onTextChange(it.text)
            }
        },
        keyboardOptions = keyboardOptions,
    )
}

@Composable
private fun timesheetTextFieldColors() = TextFieldDefaults.colors(
    focusedTextColor = MaterialTheme.colorScheme.tertiary,
    unfocusedTextColor = MaterialTheme.colorScheme.onBackground,
    focusedLabelColor = MaterialTheme.colorScheme.tertiary,
    focusedContainerColor = MaterialTheme.colorScheme.background,
    unfocusedContainerColor = MaterialTheme.colorScheme.background,
    focusedIndicatorColor = MaterialTheme.colorScheme.tertiary,
    unfocusedIndicatorColor = MaterialTheme.colorScheme.primary,
    cursorColor = MaterialTheme.colorScheme.tertiary
)
